import { startOfMonth, subMonths } from "date-fns";
import { ArrowLeftRight, Calendar, Tag, User, Users } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Accordion } from "@/components/ui/accordion";

import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import {
  LEAD_DIRECTION_CHOICES,
  LEAD_SOURCE_CHOICES,
  LEAD_STATUS_CHOICES,
  LEAD_TYPE_CHOICES,
} from "./leadConsts";

const BTN = "w-auto md:w-full justify-between h-10 md:h-8";

export const LeadListFilter = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.leads.filters.search", {
          _: "Search leads…",
        }),
      }}
    >
      <Accordion type="multiple" className="w-full">
        {/* Status */}
        <FilterCategory
          label="resources.leads.fields.status"
          icon={<Tag className="h-4 w-4" />}
          accordionValue="status"
        >
          {LEAD_STATUS_CHOICES.map((s) => (
            <ToggleFilterButton
              key={s.id}
              className={BTN}
              label={s.name}
              value={{ status: s.id }}
            />
          ))}
        </FilterCategory>

        {/* Lead Type */}
        <FilterCategory
          label="resources.leads.fields.lead_type"
          icon={<ArrowLeftRight className="h-4 w-4" />}
          accordionValue="lead_type"
        >
          {LEAD_TYPE_CHOICES.map((t) => (
            <ToggleFilterButton
              key={t.id}
              className={BTN}
              label={t.name}
              value={{ lead_type: t.id }}
            />
          ))}
        </FilterCategory>

        {/* Direction */}
        <FilterCategory
          label="resources.leads.fields.lead_direction"
          icon={<ArrowLeftRight className="h-4 w-4" />}
          accordionValue="lead_direction"
        >
          {LEAD_DIRECTION_CHOICES.map((d) => (
            <ToggleFilterButton
              key={d.id}
              className={BTN}
              label={d.name}
              value={{ lead_direction: d.id }}
            />
          ))}
        </FilterCategory>

        {/* Source */}
        <FilterCategory
          label="resources.leads.fields.lead_source"
          icon={<Tag className="h-4 w-4" />}
          accordionValue="lead_source"
        >
          {LEAD_SOURCE_CHOICES.map((s) => (
            <ToggleFilterButton
              key={s.id}
              className={BTN}
              label={s.name}
              value={{ lead_source: s.id }}
            />
          ))}
        </FilterCategory>

        {/* Owner */}
        <FilterCategory
          label="resources.leads.filters.owner"
          icon={<Users className="h-4 w-4" />}
          accordionValue="owner"
        >
          {identity && (
            <ToggleFilterButton
              className={BTN}
              label={translate("resources.leads.filters.my_leads", {
                _: "My leads",
              })}
              value={{ sales_id: identity.id }}
            />
          )}
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.leads.filters.unassigned", {
              _: "Unassigned",
            })}
            value={{ "sales_id@is": "null" }}
          />
        </FilterCategory>

        {/* Created date */}
        <FilterCategory
          label="resources.leads.fields.created_at"
          icon={<Calendar className="h-4 w-4" />}
          accordionValue="created_at"
        >
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.leads.filters.this_month", {
              _: "This month",
            })}
            value={{
              "created_at@gte": startOfMonth(new Date())
                .toISOString()
                .split("T")[0],
            }}
          />
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.leads.filters.last_3_months", {
              _: "Last 3 months",
            })}
            value={{
              "created_at@gte": subMonths(new Date(), 3)
                .toISOString()
                .split("T")[0],
            }}
          />
        </FilterCategory>

        {/* Converted */}
        <FilterCategory
          label="resources.leads.filters.conversion"
          icon={<User className="h-4 w-4" />}
          accordionValue="conversion"
        >
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.leads.filters.converted", {
              _: "Converted",
            })}
            value={{ "converted_company_id@not.is": "null" }}
          />
          <ToggleFilterButton
            className={BTN}
            label={translate("resources.leads.filters.not_converted", {
              _: "Not converted",
            })}
            value={{ "converted_company_id@is": "null" }}
          />
        </FilterCategory>
      </Accordion>
    </ResponsiveFilters>
  );
};
